import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuthApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/PageHeader';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/components/ui/use-toast';
import demoStudioService from '@/lib/api/demoStudio';
import contentLibraryService, {
  type PromptReviewCandidate,
  type PromptReviewDecision,
  type PromptReviewDecisionInput,
  type PromptReviewListResponse,
} from '@/lib/api/contentLibrary';
import { getBrowserTimezone } from '@/lib/timezone';
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Clapperboard,
  Clock,
  File as FileIcon,
  ListChecks,
  Loader2,
  MonitorPlay,
  Play,
  RotateCw,
  Trash2,
  Upload,
  Video,
  X,
  XCircle,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const buildDefaultRecordingName = (organizationName?: string | null) =>
  organizationName ? `${organizationName} demo recording` : 'Demo recording';

const REVIEW_DECISION_LABELS: Record<PromptReviewDecision, string> = {
  approve: 'Add to segment catalog',
  replace_matched: 'Replace match',
  library_only: 'Library only',
  ignore: 'Ignore',
};

const getDefaultDecision = (candidate: PromptReviewCandidate): PromptReviewDecision => {
  switch (candidate.recommended_action) {
    case 'possible_duplicate':
      return 'library_only';
    case 'possible_replacement':
      return candidate.matched_segment ? 'replace_matched' : 'approve';
    case 'library_only_candidate':
      return 'library_only';
    case 'new_candidate':
    default:
      return 'approve';
  }
};

const formatSessionTime = (value?: string | null) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatSegmentTimestamp = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '0:00';
  const totalSeconds = Math.max(0, Math.floor(value));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const recommendationBadgeCopy: Record<string, string> = {
  possible_duplicate: 'Possible duplicate',
  possible_replacement: 'Possible replacement',
  new_candidate: 'New candidate',
  library_only_candidate: 'Library only',
};

const VALID_REVIEW_DECISIONS = new Set<PromptReviewDecision>([
  'approve',
  'replace_matched',
  'library_only',
  'ignore',
]);

const isValidReviewDecision = (value: unknown): value is PromptReviewDecision =>
  typeof value === 'string' && VALID_REVIEW_DECISIONS.has(value as PromptReviewDecision);

const STATUS_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  complete: { icon: CheckCircle2, color: 'text-emerald-400', label: 'Complete' },
  processing: { icon: Loader2, color: 'text-amber-400', label: 'Processing' },
  failed: { icon: XCircle, color: 'text-red-400', label: 'Failed' },
  active: { icon: Circle, color: 'text-blue-400', label: 'Active' },
  abandoned: { icon: Clock, color: 'text-zinc-400', label: 'Expired' },
};

/* ------------------------------------------------------------------ */
/*  Animation variants                                                */
/* ------------------------------------------------------------------ */

const stagger = {
  animate: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

const fadeUp = {
  initial: { opacity: 0, y: 12, scale: 0.98 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
  },
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */

const DemoStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { userData } = useAuth();
  const { toast } = useToast();
  const [launching, setLaunching] = React.useState(false);
  const [recordingTitle, setRecordingTitle] = React.useState('');
  const [initialUrl, setInitialUrl] = React.useState('');
  const [seedFiles, setSeedFiles] = React.useState<File[]>([]);
  const [dragOver, setDragOver] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<{ done: number; total: number } | null>(
    null,
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [reviewSessionId, setReviewSessionId] = React.useState<string | null>(null);
  const [reviewDecisions, setReviewDecisions] = React.useState<
    Record<string, PromptReviewDecision>
  >({});
  const [reviewGroupOverrides, setReviewGroupOverrides] = React.useState<
    Record<string, string>
  >({});
  const [deleteSegmentsSessionId, setDeleteSegmentsSessionId] = React.useState<string | null>(null);

  React.useEffect(() => {
    setRecordingTitle((currentValue) =>
      currentValue.trim() ? currentValue : buildDefaultRecordingName(currentOrganization?.name),
    );
  }, [currentOrganization?.name]);

  /* ---- Queries ---- */

  const sessionsQuery = useQuery({
    queryKey: ['demo-studio-sessions', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('Organization required');
      const response = await demoStudioService.listSessions(currentOrganization.id, {
        limit: 12,
        offset: 0,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load demo studio sessions.');
      }
      return response.data;
    },
    enabled: Boolean(currentOrganization?.id),
    refetchInterval: 5000,
  });

  const reviewSession = React.useMemo(
    () => sessionsQuery.data?.sessions.find((s) => s.id === reviewSessionId) ?? null,
    [reviewSessionId, sessionsQuery.data?.sessions],
  );

  const reviewQuery = useQuery({
    queryKey: [
      'demo-studio-prompt-review',
      reviewSession?.organization_video_id,
      currentOrganization?.id,
    ],
    queryFn: async () => {
      if (!reviewSession?.organization_video_id || !currentOrganization?.id) {
        throw new Error('Video and organization required');
      }
      const response = await contentLibraryService.getVideoPromptReview(
        reviewSession.organization_video_id,
        currentOrganization.id,
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load prompt review candidates.');
      }
      return response.data;
    },
    enabled: Boolean(reviewSession?.organization_video_id && currentOrganization?.id),
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (!reviewQuery.data) {
      setReviewDecisions({});
      setReviewGroupOverrides({});
      isRefreshingRef.current = false;
      prevOverridesRef.current = '';
      return;
    }

    // When the data change comes from a background refresh triggered by
    // group overrides, preserve the reviewer's local decisions and group
    // overrides instead of resetting them from server data.
    if (isRefreshingRef.current) {
      isRefreshingRef.current = false;
      return;
    }

    const next: Record<string, PromptReviewDecision> = {};
    const groups: Record<string, string> = {};
    for (const c of reviewQuery.data.candidates) {
      next[c.segment_id] = isValidReviewDecision(c.review_decision)
        ? c.review_decision
        : getDefaultDecision(c);
      if (c.playlist_group) {
        groups[c.segment_id] = c.playlist_group;
      }
    }
    setReviewDecisions(next);
    setReviewGroupOverrides(groups);
    // Sync the debounce ref so the auto-refresh effect does not fire
    // spuriously when the dialog first opens with server-provided groups.
    const meaningfulInit = Object.fromEntries(
      Object.entries(groups).filter(([, v]) => v.trim()),
    );
    prevOverridesRef.current = JSON.stringify(meaningfulInit);
  }, [reviewQuery.data]);

  const hasAnyGroups = React.useMemo(
    () =>
      (reviewQuery.data?.available_groups?.length ?? 0) > 0 ||
      reviewQuery.data?.candidates.some((c) => c.playlist_group) ||
      false,
    [reviewQuery.data],
  );

  const availableGroups = React.useMemo(() => {
    if (!reviewQuery.data) return [];
    const groups = new Set<string>();
    // Include org-wide groups from the API (covers existing catalog segments).
    for (const g of reviewQuery.data.available_groups ?? []) {
      groups.add(g);
    }
    // Include groups from the current candidates.
    for (const c of reviewQuery.data.candidates) {
      if (c.playlist_group) groups.add(c.playlist_group);
    }
    // Also include any overrides the user has typed.
    for (const g of Object.values(reviewGroupOverrides)) {
      if (g.trim()) groups.add(g.trim());
    }
    return Array.from(groups).sort();
  }, [reviewQuery.data, reviewGroupOverrides]);

  /* ---- Group-scoped refresh ---- */

  const refreshReviewMutation = useMutation({
    mutationFn: async (overrides: Record<string, string>) => {
      if (!reviewSession?.organization_video_id || !currentOrganization?.id) {
        throw new Error('Review context unavailable');
      }
      const response = await contentLibraryService.refreshVideoPromptReview(
        reviewSession.organization_video_id,
        { organization_id: currentOrganization.id, group_overrides: overrides },
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to refresh recommendations.');
      }
      return response.data;
    },
    onSuccess: (data: PromptReviewListResponse) => {
      // Replace the cached review query data with the refreshed results.
      queryClient.setQueryData(
        ['demo-studio-prompt-review', reviewSession?.organization_video_id, currentOrganization?.id],
        data,
      );
    },
    onError: () => {
      // Reset the flag so subsequent reviewQuery.data changes are not blocked.
      isRefreshingRef.current = false;
    },
  });

  // Debounced auto-refresh: when the reviewer assigns or changes groups,
  // re-run recommendations scoped to those groups after a short delay.
  const refreshTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevOverridesRef = React.useRef<string>('');
  const isRefreshingRef = React.useRef(false);

  React.useEffect(() => {
    // Only trigger for non-empty overrides with at least one non-empty value.
    const meaningful = Object.fromEntries(
      Object.entries(reviewGroupOverrides).filter(([, v]) => v.trim()),
    );
    const serialized = JSON.stringify(meaningful);
    if (serialized === prevOverridesRef.current || Object.keys(meaningful).length === 0) {
      return;
    }
    prevOverridesRef.current = serialized;

    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      isRefreshingRef.current = true;
      refreshReviewMutation.mutate(meaningful);
    }, 1200);

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [reviewGroupOverrides]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- Mutations ---- */

  const applyReviewMutation = useMutation({
    mutationFn: async () => {
      if (!reviewSession?.organization_video_id || !currentOrganization?.id || !reviewQuery.data) {
        throw new Error('Review context unavailable');
      }
      const decisions: PromptReviewDecisionInput[] = reviewQuery.data.candidates
        .filter((c) => c.review_status === 'review_pending')
        .map((c) => {
          const d: PromptReviewDecisionInput = {
            segment_id: c.segment_id,
            decision: reviewDecisions[c.segment_id] ?? getDefaultDecision(c),
          };
          const groupOverride = reviewGroupOverrides[c.segment_id]?.trim();
          if (groupOverride !== undefined) {
            d.target_playlist_group = groupOverride || null;
          }
          return d;
        });
      if (!decisions.length) throw new Error('No pending decisions to apply.');
      const response = await contentLibraryService.applyVideoPromptReview(
        reviewSession.organization_video_id,
        { organization_id: currentOrganization.id, decisions },
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to apply prompt review.');
      }
      return response.data;
    },
    onSuccess: (data) => {
      toast({
        title: 'Review applied',
        description: `${data.updated} segment${data.updated === 1 ? '' : 's'} updated.`,
      });
      void queryClient.invalidateQueries({
        queryKey: ['demo-studio-sessions', currentOrganization?.id],
      });
      void queryClient.invalidateQueries({
        queryKey: [
          'demo-studio-prompt-review',
          reviewSession?.organization_video_id,
          currentOrganization?.id,
        ],
      });
      setReviewSessionId(null);
    },
    onError: (error) => {
      toast({
        title: 'Review failed',
        description: error instanceof Error ? error.message : 'Failed to apply review.',
        variant: 'destructive',
      });
    },
  });

  const deleteSegmentsMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const session = sessionsQuery.data?.sessions.find((s) => s.id === sessionId);
      if (!session?.organization_video_id || !currentOrganization?.id) {
        throw new Error('Video and organization required');
      }
      // Fetch all segmentations for this video.
      const segRes = await contentLibraryService.listVideoSegmentations(
        session.organization_video_id,
        currentOrganization.id,
      );
      if (!segRes.success || !segRes.data?.segmentations.length) {
        throw new Error('No segmentations found for this video.');
      }
      // Delete segments from every segmentation so nothing is left behind.
      let totalDeleted = 0;
      for (const seg of segRes.data.segmentations) {
        const response = await contentLibraryService.bulkDeleteSegments(
          session.organization_video_id,
          seg.segmentation_id,
          currentOrganization.id,
        );
        if (!response.success || !response.data) {
          throw new Error(response.error || `Failed to delete segments from segmentation ${seg.segmentation_id}.`);
        }
        totalDeleted += response.data.deleted_count;
      }
      return { deleted_count: totalDeleted };
    },
    onSuccess: (data) => {
      toast({
        title: 'Segments deleted',
        description: `${data.deleted_count} segment(s) removed. Catalog updated.`,
      });
      void queryClient.invalidateQueries({
        queryKey: ['demo-studio-sessions', currentOrganization?.id],
      });
      setDeleteSegmentsSessionId(null);
    },
    onError: (error) => {
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Failed to delete segments.',
        variant: 'destructive',
      });
      setDeleteSegmentsSessionId(null);
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await demoStudioService.retryFinalization(sessionId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to retry finalization.');
      }
      return response.data;
    },
    onSuccess: () => {
      toast({ title: 'Retry started', description: 'Finalization is re-running in the background.' });
      void queryClient.invalidateQueries({
        queryKey: ['demo-studio-sessions', currentOrganization?.id],
      });
    },
    onError: (error) => {
      toast({
        title: 'Retry failed',
        description: error instanceof Error ? error.message : 'Failed to retry finalization.',
        variant: 'destructive',
      });
    },
  });

  /* ---- Handlers ---- */

  const handleAddSeedFiles = React.useCallback(
    (files: FileList | File[]) => {
      const MAX_SEED_FILE_BYTES = 25 * 1024 * 1024;
      const incoming = Array.from(files);
      if (incoming.length === 0) return;
      const accepted: File[] = [];
      const rejected: string[] = [];
      for (const file of incoming) {
        if (file.size > MAX_SEED_FILE_BYTES) {
          rejected.push(file.name);
        } else {
          accepted.push(file);
        }
      }
      if (rejected.length > 0) {
        toast({
          title: rejected.length === 1 ? 'File too large' : 'Some files too large',
          description:
            rejected.length === 1
              ? `${rejected[0]} exceeds the 25MB limit.`
              : `These files exceed the 25MB limit: ${rejected.join(', ')}`,
          variant: 'destructive',
        });
      }
      if (accepted.length > 0) {
        setSeedFiles((prev) => [...prev, ...accepted]);
      }
    },
    [toast],
  );

  const handleRemoveSeedFile = React.useCallback((index: number) => {
    setSeedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSeedDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        handleAddSeedFiles(e.dataTransfer.files);
      }
    },
    [handleAddSeedFiles],
  );

  const handleLaunch = React.useCallback(async () => {
    if (!currentOrganization?.id) {
      toast({ title: 'No organization selected', variant: 'destructive' });
      return;
    }
    if (!recordingTitle.trim()) {
      toast({ title: 'Recording name required', variant: 'destructive' });
      return;
    }
    if (!initialUrl.trim()) {
      toast({ title: 'Starting page required', variant: 'destructive' });
      return;
    }

    setLaunching(true);
    try {
      const response = await demoStudioService.createSession({
        organization_id: currentOrganization.id,
        title: recordingTitle.trim(),
        timezone: userData?.timezone || getBrowserTimezone(),
        initial_url: initialUrl.trim(),
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to launch Demo Studio.');
      }
      const newSessionId = response.data.id;

      let filesAttached = 0;
      if (seedFiles.length > 0) {
        setUploadProgress({ done: 0, total: seedFiles.length });
        for (let i = 0; i < seedFiles.length; i++) {
          const file = seedFiles[i];
          try {
            const uploadResponse = await demoStudioService.uploadFileToBrowser(newSessionId, file);
            if (!uploadResponse.success) {
              throw new Error(uploadResponse.error || `Failed to upload ${file.name}`);
            }
            filesAttached++;
          } catch (uploadError) {
            toast({
              title: `Couldn't attach ${file.name}`,
              description:
                uploadError instanceof Error
                  ? uploadError.message
                  : 'Upload failed — you can still continue recording.',
              variant: 'destructive',
            });
          }
          setUploadProgress({ done: i + 1, total: seedFiles.length });
        }
      }

      if (seedFiles.length > 0 && filesAttached === 0) {
        toast({
          title: 'Studio launched without files',
          description:
            'None of the selected files could be attached. File inputs in the browser won\u2019t have files available.',
          variant: 'destructive',
          duration: 8000,
        });
      } else {
        toast({ title: 'Studio launched', description: 'Browser session is ready.' });
      }
      sessionStorage.setItem(
        `demo-studio-launch:${newSessionId}`,
        JSON.stringify({ title: recordingTitle.trim(), initialUrl: initialUrl.trim() }),
      );
      navigate(`/demo-studio/sessions/${newSessionId}`);
    } catch (error) {
      toast({
        title: 'Launch failed',
        description: error instanceof Error ? error.message : 'Failed to launch.',
        variant: 'destructive',
      });
    } finally {
      setLaunching(false);
      setUploadProgress(null);
    }
  }, [currentOrganization?.id, initialUrl, navigate, recordingTitle, seedFiles, toast, userData?.timezone]);

  const recentSessions = sessionsQuery.data?.sessions ?? [];
  const canLaunch =
    Boolean(currentOrganization?.id) &&
    recordingTitle.trim().length > 0 &&
    initialUrl.trim().length > 0;

  /* ---- Render ---- */

  return (
    <>
      <Helmet>
        <title>Studio | Hobbes</title>
      </Helmet>

      <div className="flex min-h-0 flex-col">
        <PageHeader
          title={<>Product Studio <HelpTooltip text="Record product walkthroughs in a managed browser." /></>}
        />

        <motion.div
          className="flex-1 px-4 py-6 sm:px-6 md:px-8"
          variants={stagger}
          initial="initial"
          animate="animate"
        >
          {/* ---- Launch Card ---- */}
          <motion.div variants={fadeUp}>
            <Card className="border card-border shadow-none overflow-hidden">
              <CardContent className="p-0">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto]">
                  {/* Left: form */}
                  <div className="p-5 space-y-4">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <MonitorPlay className="h-4 w-4" />
                      </div>
                      <h2 className="text-sm font-medium text-foreground">New recording</h2>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="demo-studio-recording-title" className="text-xs">
                          Recording name
                        </Label>
                        <Input
                          id="demo-studio-recording-title"
                          type="text"
                          placeholder="Q2 product overview"
                          value={recordingTitle}
                          onChange={(e) => setRecordingTitle(e.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="demo-studio-initial-url" className="text-xs">
                          Starting page
                        </Label>
                        <Input
                          id="demo-studio-initial-url"
                          type="url"
                          placeholder="https://app.example.com/login"
                          value={initialUrl}
                          onChange={(e) => setInitialUrl(e.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                          className="h-9"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">
                          Attachments{' '}
                          <span className="font-normal text-muted-foreground">(optional)</span>
                        </Label>
                        <HelpTooltip text="Files added here are available in the browser session — pick one when a file picker opens during recording." />
                      </div>
                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleSeedDrop}
                        className={`flex items-center gap-3 rounded-md border border-dashed px-3 py-2.5 transition ${
                          dragOver
                            ? 'border-primary/60 bg-primary/5'
                            : 'border-border hover:border-foreground/30'
                        }`}
                      >
                        <Upload className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        <span className="flex-1 text-xs text-muted-foreground">
                          Drag files here or browse
                        </span>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={launching}
                          className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
                        >
                          Browse
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files) {
                              handleAddSeedFiles(e.target.files);
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>
                      {seedFiles.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {seedFiles.map((file, index) => (
                            <span
                              key={`${file.name}-${index}`}
                              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/50 py-0.5 pl-2 pr-1 text-xs text-foreground"
                            >
                              <FileIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                              <span className="max-w-[180px] truncate">{file.name}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveSeedFile(index)}
                                disabled={launching}
                                aria-label={`Remove ${file.name}`}
                                className="rounded p-0.5 text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-50"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: launch */}
                  <div className="flex items-center border-t lg:border-t-0 lg:border-l border-border/60 bg-muted/20 px-8 py-5">
                    <motion.div
                      whileHover={canLaunch && !launching ? { scale: 1.02 } : undefined}
                      whileTap={canLaunch && !launching ? { scale: 0.97 } : undefined}
                    >
                      <Button
                        size="lg"
                        onClick={handleLaunch}
                        disabled={launching || !canLaunch}
                        className="gap-2 px-6 h-10"
                      >
                        {launching ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {uploadProgress
                              ? `Uploading files (${uploadProgress.done}/${uploadProgress.total})…`
                              : 'Launching…'}
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4" />
                            Launch Studio
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* ---- Recordings ---- */}
          <motion.div variants={fadeUp} className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-medium text-foreground">Recordings</h2>
                {sessionsQuery.isFetching && (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                )}
              </div>
              {recentSessions.length > 0 && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {recentSessions.length} session{recentSessions.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {sessionsQuery.isLoading ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-[128px] rounded-xl border border-border/50 bg-muted/20 animate-pulse"
                  />
                ))}
              </div>
            ) : recentSessions.length === 0 ? (
              <Card className="border border-dashed card-border shadow-none">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/50 mb-3">
                    <Video className="h-5 w-5 text-muted-foreground/60" />
                  </div>
                  <p className="text-sm text-muted-foreground">No recordings yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <AnimatePresence mode="popLayout">
                  {recentSessions.map((session, idx) => {
                    const hasVideo = Boolean(session.organization_video_id);
                    const cfg = STATUS_CONFIG[session.status] ?? {
                      icon: Circle,
                      color: 'text-muted-foreground',
                      label: session.status,
                    };
                    const StatusIcon = cfg.icon;

                    return (
                      <motion.div
                        key={session.id}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{
                          duration: 0.3,
                          delay: idx * 0.04,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                      >
                        <Card className="group border card-border shadow-none hover:border-primary/20 transition-colors duration-200">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-medium text-foreground truncate">
                                  {session.title || 'Untitled recording'}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <StatusIcon
                                    className={`h-3 w-3 ${cfg.color} ${session.status === 'processing' ? 'animate-spin' : ''}`}
                                  />
                                  <span className="text-xs text-muted-foreground">
                                    {cfg.label}
                                  </span>
                                </div>
                              </div>
                              <span className="text-xs text-muted-foreground/70 shrink-0 tabular-nums">
                                {formatSessionTime(session.updated_at)}
                              </span>
                            </div>

                            {hasVideo && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0 h-5 gap-1"
                              >
                                <CheckCircle2 className="h-2.5 w-2.5" />
                                Video ready
                              </Badge>
                            )}

                            <div className="flex items-center gap-2 pt-2 border-t border-border/40">
                              {session.organization_video_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs px-2 gap-1.5"
                                  onClick={() =>
                                    navigate(
                                      `/content-library/video/${session.organization_video_id}`,
                                    )
                                  }
                                >
                                  <Clapperboard className="h-3 w-3" />
                                  View
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs px-2 gap-1.5"
                                disabled={!hasVideo}
                                onClick={() => setReviewSessionId(session.id)}
                              >
                                <ListChecks className="h-3 w-3" />
                                Review
                              </Button>
                              {hasVideo && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs px-2 gap-1.5 text-red-500 hover:text-red-600"
                                  disabled={deleteSegmentsMutation.isPending && deleteSegmentsMutation.variables === session.id}
                                  onClick={() => setDeleteSegmentsSessionId(session.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                  Delete segments
                                </Button>
                              )}
                              {(session.status === 'processing' || session.status === 'failed') &&
                                !session.organization_video_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs px-2 gap-1.5 text-amber-500 hover:text-amber-600"
                                  disabled={retryMutation.isPending && retryMutation.variables === session.id}
                                  onClick={() => retryMutation.mutate(session.id)}
                                >
                                  <RotateCw className={`h-3 w-3 ${retryMutation.isPending && retryMutation.variables === session.id ? 'animate-spin' : ''}`} />
                                  Retry
                                </Button>
                              )}
                              {hasVideo && (
                                <ArrowRight className="h-3 w-3 text-muted-foreground/40 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* ---- Delete Segments Confirmation ---- */}
      <AlertDialog
        open={Boolean(deleteSegmentsSessionId)}
        onOpenChange={(open) => !open && setDeleteSegmentsSessionId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all segments?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove all segments from this video and update the catalog.
              The video itself will be kept. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteSegmentsMutation.isPending}
              onClick={() => {
                if (deleteSegmentsSessionId) {
                  deleteSegmentsMutation.mutate(deleteSegmentsSessionId);
                }
              }}
            >
              {deleteSegmentsMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete all segments'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---- Review Dialog ---- */}
      <Dialog
        open={Boolean(reviewSessionId)}
        onOpenChange={(open) => !open && setReviewSessionId(null)}
      >
        <DialogContent className="max-w-5xl overflow-hidden p-0">
          <DialogHeader className="border-b px-6 py-5">
            <DialogTitle>Review segmented assets</DialogTitle>
            <DialogDescription>
              Decide which clips update the demo catalog and which stay in the library.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[75vh] overflow-y-auto px-6 py-5">
            {reviewQuery.isLoading ? (
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading candidates…
              </div>
            ) : reviewQuery.data?.candidates.length ? (
              <div className="space-y-4">
                {/* Bulk group assignment for all pending segments */}
                {hasAnyGroups && reviewQuery.data.candidates.some((c) => c.review_status === 'review_pending') && (
                  <div className="flex items-center gap-3 rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
                    <Label htmlFor="bulk-group-select" className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                      Set group for all segments
                    </Label>
                    <Input
                      id="bulk-group-select"
                      type="text"
                      placeholder="e.g. Product Overview"
                      list="playlist-group-options"
                      className="h-7 text-xs max-w-[220px]"
                      onChange={(e) => {
                        const group = e.target.value;
                        if (!group.trim()) return;
                        const pendingIds = reviewQuery.data!.candidates
                          .filter((c) => c.review_status === 'review_pending')
                          .map((c) => c.segment_id);
                        setReviewGroupOverrides((cur) => {
                          const next = { ...cur };
                          for (const id of pendingIds) {
                            next[id] = group;
                          }
                          return next;
                        });
                      }}
                    />
                    {refreshReviewMutation.isPending && (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    )}
                  </div>
                )}
                {reviewQuery.data.candidates.map((candidate) => {
                  const decision =
                    reviewDecisions[candidate.segment_id] ?? getDefaultDecision(candidate);
                  const pending = candidate.review_status === 'review_pending';
                  return (
                    <div
                      key={candidate.segment_id}
                      className="rounded-2xl border border-border/70 p-4"
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">
                              {candidate.label || 'Untitled segment'}
                            </span>
                            <Badge variant={pending ? 'secondary' : 'outline'}>
                              {candidate.review_status || 'unknown'}
                            </Badge>
                            {candidate.recommended_action && (
                              <Badge variant="outline">
                                {recommendationBadgeCopy[candidate.recommended_action] ??
                                  candidate.recommended_action}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {candidate.description || candidate.reason || 'No description.'}
                          </p>
                          <div className="text-xs text-muted-foreground tabular-nums">
                            {candidate.start_time.toFixed(1)}s – {candidate.end_time.toFixed(1)}s
                            {typeof candidate.confidence === 'number'
                              ? ` · ${candidate.confidence.toFixed(2)}`
                              : ''}
                          </div>

                          {hasAnyGroups && pending && (
                            <div className="mt-2 flex items-center gap-2">
                              <Label htmlFor={`group-${candidate.segment_id}`} className="text-xs text-muted-foreground whitespace-nowrap">Group</Label>
                              <Input
                                id={`group-${candidate.segment_id}`}
                                type="text"
                                placeholder="e.g. Product Overview"
                                value={reviewGroupOverrides[candidate.segment_id] ?? candidate.playlist_group ?? ''}
                                onChange={(e) =>
                                  setReviewGroupOverrides((cur) => ({
                                    ...cur,
                                    [candidate.segment_id]: e.target.value,
                                  }))
                                }
                                list="playlist-group-options"
                                className="h-7 text-xs max-w-[220px]"
                              />
                              {refreshReviewMutation.isPending && (
                                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                              )}
                            </div>
                          )}
                          {hasAnyGroups && !pending && candidate.playlist_group && (
                            <Badge variant="outline" className="mt-2 text-xs">
                              {candidate.playlist_group}
                            </Badge>
                          )}

                          {candidate.matched_segment && (
                            <div className="rounded-xl border border-border/70 bg-muted/20 p-3 mt-2">
                              <div className="mb-3 flex flex-wrap items-center gap-2">
                                <Badge variant="secondary">
                                  {candidate.recommended_action === 'possible_duplicate'
                                    ? 'Possible duplicate'
                                    : 'Possible replacement'}
                                </Badge>
                              </div>
                              <div className="grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
                                <div className="overflow-hidden rounded-lg border border-border/70 bg-muted/30">
                                  {candidate.matched_segment.thumbnail_url ? (
                                    <img
                                      src={candidate.matched_segment.thumbnail_url}
                                      alt={candidate.matched_segment.label || 'Segment thumbnail'}
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <div className="flex aspect-video items-center justify-center text-xs text-muted-foreground">
                                      No preview
                                    </div>
                                  )}
                                </div>
                                <div className="space-y-1.5 text-sm">
                                  <div className="font-medium text-foreground">
                                    {candidate.matched_segment.label || 'Untitled'}
                                  </div>
                                  {candidate.matched_segment.video_title && (
                                    <div className="text-muted-foreground">
                                      From:{' '}
                                      <span className="text-foreground">
                                        {candidate.matched_segment.video_title}
                                      </span>
                                    </div>
                                  )}
                                  <div className="text-muted-foreground tabular-nums">
                                    {formatSegmentTimestamp(candidate.matched_segment.start_time)} –{' '}
                                    {formatSegmentTimestamp(candidate.matched_segment.end_time)}
                                    {typeof candidate.matched_segment.duration === 'number'
                                      ? ` · ${candidate.matched_segment.duration.toFixed(1)}s`
                                      : ''}
                                  </div>
                                  {candidate.matched_segment.video_id && (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="mt-1"
                                      onClick={() =>
                                        navigate(
                                          `/content-library/video/${candidate.matched_segment?.video_id}`,
                                        )
                                      }
                                    >
                                      Open existing asset
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2 lg:max-w-sm lg:justify-end">
                          {(
                            ['approve', 'replace_matched', 'library_only', 'ignore'] as PromptReviewDecision[]
                          )
                            .filter((d) => d !== 'replace_matched' || Boolean(candidate.matched_segment))
                            .map((d) => (
                              <Button
                                key={d}
                                type="button"
                                variant={decision === d ? 'default' : 'outline'}
                                size="sm"
                                onClick={() =>
                                  setReviewDecisions((cur) => ({
                                    ...cur,
                                    [candidate.segment_id]: d,
                                  }))
                                }
                              >
                                {REVIEW_DECISION_LABELS[d]}
                              </Button>
                            ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-border/80 px-4 py-8 text-center text-sm text-muted-foreground">
                No review candidates for this recording.
              </div>
            )}
            {hasAnyGroups && availableGroups.length > 0 && (
              <datalist id="playlist-group-options">
                {availableGroups.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            )}
          </div>

          <DialogFooter className="border-t px-6 py-4">
            <Button variant="outline" onClick={() => setReviewSessionId(null)}>
              Close
            </Button>
            <Button
              onClick={() => applyReviewMutation.mutate()}
              disabled={
                applyReviewMutation.isPending ||
                reviewQuery.isLoading ||
                !reviewQuery.data?.pending_candidates
              }
              className="gap-2"
            >
              {applyReviewMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Applying…
                </>
              ) : (
                <>
                  <ListChecks className="h-4 w-4" />
                  Apply decisions
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DemoStudioPage;
